/* @flow */
/**
 * External dependencies
 */
import { isArray, noop } from 'lodash';
import React, { Component } from 'react';

/**
 * Flow
 */
export type IconProps = {
	className?: string,
	icon:
		'arrow-down' |
		'arrow-left' |
		'arrow-left-alt' |
		'arrow-right' |
		'checkmark' |
		'headphones' |
		'play' |
		'play-circle' |
		'pause' |
		'sound',
	size: number,
	onClick: Function,
};

export default class Icon extends Component<IconProps> {
	static defaultProps = {
		size: 24,
		onClick: noop,
	};

	shouldComponentUpdate( nextProps: IconProps ) {
		return (
			this.props.icon !== nextProps.icon ||
			this.props.size !== nextProps.size ||
			this.props.className !== nextProps.className
		);
	}

	renderPath( path: string | Array<string>, icon: string ) {
		if ( isArray( path ) ) {
			return path.map( ( p, index ) => <path key={ `${ icon }-${ index }` } d={ p } /> );
		}

		return <path d={ path } />;
	}

	render() {
		const { icon, className, size = 20, onClick } = this.props;
		let path;
		let viewbox = '0 0 24 24';

		// eslint-disable-next-line
		switch ( icon ) {
			case 'arrow-down':
				path = 'M20 15.4a.6.6 0 0 0-.8 0L12.6 22V.6c0-.3-.3-.6-.6-.6s-.6.3-.6.6v21.5l-6.6-6.6a.6.6 0 0 0-.8 0c-.2.2-.2.6 0 .8l7.6 7.6c.1.1.3.2.4.2s.3-.1.4-.2l7.6-7.6c.2-.3.2-.7 0-.9z';
				break;

			case 'arrow-left':
				path = 'M2 12l5.4-5.4v4.5H22v1.8H7.4v4.5L2 12z';
				break;

			case 'arrow-right':
				path = 'M22,12l-5.4,5.4v-4.5H2v-1.8h14.6V6.6L22,12z';
				break;

			case 'arrow-left-alt':
				path = 'M11.9 21.7L2 12.1V12l9.9-9.6L13.4 4l-7.3 6.8H22v2.5H6.1l7.3 6.8-1.5 1.6z';
				break;

			case 'checkmark':
				path = 'M8.8 22c-.6 0-1.2-.3-1.5-.8l-4.7-7.1c-.6-.8-.3-2 .5-2.6.8-.6 2-.3 2.6.5l3.2 4.8 9.6-14.1c.5-.7 1.7-1 2.5-.4s1 1.7.5 2.6L10.3 21.2c-.3.5-.9.8-1.5.8z';
				break;

			case 'headphones':
				path = 'M121.5 83.6l-.7-.6 1.6-6.7C130.3 43 109.6 9.6 76.3 1.7c-23.4-5.5-47.9 3-62.8 21.9A61.09 61.09 0 0 0 1.7 76.2l1.6 6.7-.7.6c-2 1.6-2.8 4.3-1.9 6.7L8.3 111c.9 2.4 3.1 3.9 5.6 3.9l6.7.3c2.3 4.4 7.5 6.4 12.2 4.7 7.4-2.6 6.9-10.3 6-12.8L26.4 73.6c-1.4-3.9-6.6-8.2-12.8-6-4.6 1.8-7.3 6.7-6.2 11.5l-.7.7-1.1-4.5c-7.4-31.2 12-62.4 43.2-69.8C70.6.4 93.6 8.3 107.5 26c11 13.9 15.1 32.1 11 49.3l-1.1 4.5-.7-.7c1.1-4.8-1.6-9.7-6.3-11.4-6.1-2.2-11.3 2-12.8 6l-12.3 33.7c-.9 2.5-1.5 10.2 6 12.8 4.7 1.7 9.9-.3 12.2-4.7l6.7-.3c2.5 0 4.8-1.6 5.6-3.9l7.6-20.8c.9-2.6.1-5.3-1.9-6.9zM15 71.4c3.4-1.3 6.8 1.2 7.7 3.6L35 108.7c.5 1.5 1.3 5.9-3.6 7.7-3.1 1.1-6.5-.5-7.7-3.6L11.4 79.1c-1.1-3.1.5-6.5 3.6-7.7zM5.2 86.6l3.5-3.3 10.2 28-5-.3c-.9 0-1.6-.5-1.9-1.3L4.4 88.9c-.3-.8 0-1.8.8-2.3zm95.2 26.2a6.18 6.18 0 0 1-7.7 3.6c-4.9-1.8-4.1-6.2-3.6-7.7L101.4 75c1.2-3 4.6-4.6 7.7-3.6 3.1 1.1 4.7 4.6 3.6 7.7l-12.3 33.7zm19.3-23.9l-7.6 20.8c-.3.8-1.1 1.3-1.9 1.3l-5 .3 10.2-28 3.5 3.3c.7.5 1.1 1.5.8 2.3z';
				viewbox = '0 0 124.1 120.7';
				break;

			case 'play-circle':
				path = [
					'M10.22 17.25c-.1 0-.2 0-.3-.1-.2-.1-.4-.3-.4-.6v-8.4c0-.3.1-.5.4-.6.2-.1.5-.1.7.1l5.3 4.2c.2.1.3.3.3.5s-.1.4-.3.5l-5.3 4.2c-.1.1-.2.2-.4.2zm.7-7.7v5.6l3.5-2.8-3.5-2.8z',
					'M12 24C5.4 24 0 18.6 0 12S5.4 0 12 0s12 5.4 12 12-5.4 12-12 12zm0-22.7a10.71 10.71 0 0 0 0 21.4 10.71 10.71 0 0 0 0-21.4z',
				];
				break;

			case 'play':
				path = 'M5.45 24.05c-.2 0-.5-.1-.7-.2-.6-.3-.9-.9-.9-1.5V1.65c0-.6.4-1.2.9-1.5.6-.3 1.2-.2 1.7.2l13.1 10.4c.4.3.6.8.6 1.3s-.2 1-.6 1.3l-13.1 10.3c-.3.2-.6.4-1 .4zm1.7-19v13.9l8.8-6.9-8.8-7z';
				break;

			case 'pause':
				path = 'M5.43 24.05c-.9 0-1.6-.7-1.6-1.6V1.65c0-.9.7-1.6 1.6-1.6s1.6.7 1.6 1.6v20.7c.1.9-.7 1.7-1.6 1.7zM18.53 24.05c-.9 0-1.6-.8-1.6-1.7V1.65c0-.9.7-1.6 1.6-1.6s1.6.7 1.6 1.6v20.7c.1.9-.7 1.7-1.6 1.7z';
				break;

			case 'sound':
				path = 'M13.8 3.4c-.2-.1-.5-.1-.7 0L6.7 7.5h-4c-.4 0-.7.3-.7.7v7.7c0 .4.3.7.7.7h4.1l6.4 4.1c.1.1.2.1.4.1.1 0 .2 0 .3-.1.2-.1.3-.3.3-.6V4c-.1-.3-.2-.5-.4-.6zM3.3 8.8h3v6.4h-3V8.8zm9.5 10l-5.2-3.3v-7l5.2-3.3v13.6zM17.5 8.5c-.4.1-.5.5-.4.8.3.9.5 1.7.5 2.7 0 .9-.1 1.8-.5 2.6-.1.3 0 .7.4.8h.2c.3 0 .5-.2.6-.4.4-1 .6-2 .6-3.1s-.2-2.1-.6-3.1c-.1-.3-.5-.4-.8-.3zM21.2 7.5c-.1-.3-.5-.5-.8-.4-.4.1-.6.5-.4.8.5 1.3.7 2.7.7 4.1 0 1.4-.3 2.8-.7 4.1-.1.3 0 .7.4.8h.2c.3 0 .5-.2.6-.4.5-1.4.8-3 .8-4.5 0-1.6-.3-3.1-.8-4.5z';
				break;
		}

		if ( ! path ) {
			return null;
		}

		const iconClass = [ 'icon', 'icons-' + icon, className ].filter( Boolean ).join( ' ' );

		return (
			<svg
				aria-hidden
				role="img"
				focusable="false"
				className={ iconClass }
				xmlns="http://www.w3.org/2000/svg"
				width={ size }
				height={ size }
				viewBox={ viewbox }
				onClick={ onClick }
			>
				{ this.renderPath( path, icon ) }
			</svg>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/icon/index.js